<template>
    <!-- One row: date navigation on the left, actions on the right. This used to be
         two rows split by an <hr>, which cost vertical space and left the date
         stranded under the controls that move it. -->
    <div class="flex flex-wrap gap-2 items-center time-navigation">
        <button v-if="hasMenu" class="tt-icon-btn tt-icon-btn--bordered" @click.stop.prevent="toggleMenu()"
            v-bind:title="texts.toggle_menu"
            v-bind:aria-label="texts.toggle_menu"
            v-bind:aria-expanded="menuOpen ? 'true' : 'false'">
            <i class="icon-menu" aria-hidden="true"></i>
        </button>

        <template v-if="view != 'schedule'">
            <button class="btn btn-sm btn-primary tt-btn-outline" @click="openToday()">
                {{ texts.today }}
            </button>

            <div class="flex items-center gap-1">
                <button type="button" class="tt-icon-btn tt-icon-btn--round tt-icon-btn--bordered"
                    v-bind:title="texts.previous" v-bind:aria-label="texts.previous"
                    @click.stop.prevent="showPrev()">❮</button>
                <button type="button" class="tt-icon-btn tt-icon-btn--round tt-icon-btn--bordered"
                    v-bind:title="texts.next" v-bind:aria-label="texts.next"
                    @click.stop.prevent="showNext()">❯</button>
            </div>
        </template>

        <span class="navi-time">{{ navigationDate }}</span>

        <atom-spinner class="navi-spinner" v-if="isLoading" :animation-duration="1000" :size="30"
            :color="'#ff1d5e'" />

        <div class="flex items-center gap-2 ml-auto settings-navigation">
            <!-- The left rail carries the same shortcut, so this one only steps in when
                 the rail is hidden -- by a small screen or by the hamburger alike. Keyed
                 off the rail's real state rather than a breakpoint, which would have to
                 be kept in sync with Menu.js by hand. -->
            <button v-if="showFreePlacesView && !menuOpen" class="btn btn-primary btn-sm"
                @click="toggleFreePlacesView()">
                {{ texts.open }} {{ view !== 'free_places' ? texts.views['free_places'] : texts.views[oldView] }}
            </button>

            <!-- The holiday controls moved to the instructor bar: they act on the
                 instructor, not on the calendar's date navigation, and here they pushed
                 this row onto two lines the moment an instructor was picked. -->

            <!-- The view picker used to be a second dropdown of its own next to this
                 one. Folded in here: two adjacent menus for one concern was a row's
                 worth of width for nothing. The current view is marked, since its name
                 is no longer on show. -->
            <!-- navi-icon / navi-icon-settings dropped on purpose: they padded a bare
                 icon into a hit target (5px 10px !important), which now just makes the
                 toggle 10px taller than the row. The label is a tt-icon-btn with its own
                 36px box. -->
            <div class="dropdown dropdown-bottom dropdown-end tt-settings-menu">
                <label tabindex="0" id="dropdownTimetableSettings" data-toggle="dropdown" aria-haspopup="true"
                    aria-expanded="false" class="tt-icon-btn tt-icon-btn--bordered"
                    v-bind:title="texts.settings" v-bind:aria-label="texts.settings">
                    <i class="icon-settings" aria-hidden="true"></i>
                </label>
                <ul tabindex="0" class="
                    dropdown-menu dropdown-menu-right dropdown-content
                    !transition-opacity shadow tt-surface z-10 !w-fit
                    tt-menu tt-menu--checkable
                " aria-labelledby="dropdownTimetableSettings">
                    <li class="tt-menu__label">{{ texts.view_label }}</li>

                    <li class="tt-menu__item" v-for="$view in views" :key="'view-' + $view"
                        :class="['timetable-view-' + $view, { 'is-current': view === $view }]"
                        v-if="
                            ($view == 'schedule' && showScheduleView) ||
                            ($view == 'month' && showMonthView) ||
                            ($view != 'schedule' && $view != 'month')
                        "
                        @click.stop.prevent="selectView($view)">
                        <a>{{ texts.views[$view] }}</a>
                    </li>

                    <li class="tt-menu__item" v-if="showFreePlacesView"
                        :class="{ 'is-current': view === 'free_places' }"
                        @click.stop.prevent="selectView('free_places')">
                        <a>{{ texts.views['free_places'] }}</a>
                    </li>

                    <li class="tt-menu__divider"></li>

                    <li class="tt-menu__item" @click.stop.prevent="reload()">
                        <a><i class="icon-reload"></i> {{ texts.reload }}</a>
                    </li>
                    <li v-if="showTrashView" class="tt-menu__item" @click.stop.prevent="showTrash()">
                        <a><i class="icon-trash"></i> {{ texts.trash }}</a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { EventBus } from "../mixins/EventBus";
import { ViewDates } from "../mixins/ViewDatesMixin";

export default {
    mixins: [EventBus, ViewDates],
    props: {
        view: {
            default: "week",
        },
        date: {
            default: () => new Date(),
        },
        isLoading: {
            default: false,
        },
        showScheduleView: {
            default: false,
        },
        showFreePlacesView: {
            default: false,
        },
        showTrashView: {
            default: true,
        },
        showMonthView: {
            default: false,
        },
        menuOpen: {
            default: true,
        },
        // Whether there is a rail to open at all. Only the calendar list has one, so
        // everywhere else the toggle was a button wired to nothing.
        hasMenu: {
            default: false,
        },
        views: {
            default: () => ["week", "day", "month", "schedule"],
        },
        translations: {
            default: () => { },
        },
    },

    data() {
        return {
            texts: {
                today: "Astăzi",
                toggle_menu: "Arată/ascunde panoul",
                settings: "Setări și vizualizare",
                view_label: "Vizualizare",
                previous: "Perioada anterioară",
                next: "Perioada următoare",
                reload: "Reîncarcă",
                trash: "Coș de gunoi",
                // Default view labels so the settings menu (v-for over `views`) never
                // reads `texts.views[...]` off undefined. A calendar embedded without
                // :translations (e.g. the instructor dashboard) otherwise threw here
                // during render, and Vue silently dropped the whole navigation bar --
                // taking the view switcher and prev/next with it. Real translations,
                // when passed, override this via the spread below.
                views: {
                    week: "Săptămână",
                    day: "Zi",
                    month: "Lună",
                    schedule: "Planificare",
                    free_places: "Locuri libere",
                },
                ...this.translations,
            },
            oldView: this.view,
        };
    },

    mounted() {
        // this.eventBus.$on('loading', this.loading);
    },

    methods: {
        openToday() {
            let date = moment();
            this.eventBus.$emit("date-changed", date.toDate());
        },

        showPrev() {
            let date = moment(this.date).add(-this.viewNumberOfDays, "days");
            this.eventBus.$emit("date-changed", date.toDate());
        },

        showNext() {
            let date = moment(this.date).add(this.viewNumberOfDays, "days");
            this.eventBus.$emit("date-changed", date.toDate());
        },

        toggleMenu() {
            this.eventBus.$emit("toggle-menu");
        },

        selectView(view) {
            this.eventBus.$emit("view-changed", view);
        },

        toggleFreePlacesView() {
            if (this.view === "free_places") {
                this.eventBus.$emit("view-changed", this.oldView);
            } else {
                this.oldView = this.view;
                this.eventBus.$emit("view-changed", "free_places");
            }
        },

        reload() {
            this.eventBus.$emit("reload-calendar");
            this.eventBus.$emit("update-events-force");
        },

        showTrash() {
            this.eventBus.$emit("show-trash-view");
        },

    },
    computed: {
        navigationDate() {
            switch (this.view) {
                case "day":
                    return moment(this.date).format("MMMM YYYY");

                case "schedule":
                    return ""; //this.viewStartDate.format('MMM YYYY') + ' - ' + this.viewEndDate.format('MMM YYYY');

                default:
                    // week view
                    if (this.viewStartDate.month() === this.viewEndDate.month()) {
                        return (
                            this.viewStartDate.format("D") +
                            " - " +
                            this.viewEndDate.format("D MMMM")
                        );
                    }
                    return (
                        this.viewStartDate.format("D MMMM") +
                        " - " +
                        this.viewEndDate.format("D MMMM")
                    );
            }
        },
    },
};
</script>
<style lang="scss">
.time-navigation .settings-navigation ul li {
    cursor: pointer;
}
</style>
